import * as z from 'zod';

export const GetScheduleTimeFrameRequest = z.object({
  _id: z
    .string()
    .describe(
      '[Schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nID for which to retrieve the schedule time frame.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      timeZone: z
        .string()
        .describe(
          'Time zone for adjusting the times of the returned schedule time frame.\n\nDefault: `timeZone` of the [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const GetScheduleTimeFrameResponse = z.object({
  _id: z
    .string()
    .describe(
      'ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the time frame belongs.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['NONE', 'FINITE', 'INFINITE'])
    .describe(
      'Information about the presence of recurring events within the schedule.\n\nSupported values:\n+ `NONE`: No [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object) are scheduled within the schedule.\n+ `FINITE`: All recurring events within the schedule have a defined end date, or there are no recurring events.\n+ `INFINITE`: At least one recurring event within the schedule has no end date, meaning it follows an unlimited repetition pattern.'
    )
    .optional(),
  firstEventStartDate: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Start date of the first [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nwithin the schedule. Identical to `event.start.localDate`. Available only if\n`status` is set to `FINITE` or `INFINITE`.'
    )
    .optional(),
  lastEventEndDate: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'End date of the last [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nbelonging to the schedule. Identical to `event.end.localDate`. Available only if\n`status` is set to `FINITE`.'
    )
    .optional(),
  adjustedFirstEventStartDate: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Start date of the first [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nbelonging to the schedule, adjusted to `timeZone` of the\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor the `timeZone` specified in the request.\nIdentical to `event.adjustedStart.localDate`.'
    )
    .optional(),
  adjustedLastEventEndDate: z
    .object({
      localDate: z
        .string()
        .describe(
          'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
        )
        .optional()
        .nullable(),
      timeZone: z
        .string()
        .describe(
          'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
        )
        .optional()
        .nullable(),
      utcDate: z
        .date()
        .describe(
          'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'End date of the last [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nbelonging to the schedule, adjusted to `timeZone` of the\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor the `timeZone` specified in the request.\nIdentical to `event.adjustedEnd.localDate`.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the schedule time frame is updated.'
    )
    .optional()
    .nullable(),
});
export const ListScheduleTimeFramesRequest = z.object({
  ids: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      timeZone: z
        .string()
        .describe(
          'Time zone for adjusting the times of the returned schedule time frames.\n\nDefault: `timeZone` of the [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListScheduleTimeFramesResponse = z.object({
  scheduleTimeFrames: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'ID of the [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/schedule-object)\nto which the time frame belongs.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['NONE', 'FINITE', 'INFINITE'])
          .describe(
            'Information about the presence of recurring events within the schedule.\n\nSupported values:\n+ `NONE`: No [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object) are scheduled within the schedule.\n+ `FINITE`: All recurring events within the schedule have a defined end date, or there are no recurring events.\n+ `INFINITE`: At least one recurring event within the schedule has no end date, meaning it follows an unlimited repetition pattern.'
          )
          .optional(),
        firstEventStartDate: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Start date of the first [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nwithin the schedule. Identical to `event.start.localDate`. Available only if\n`status` is set to `FINITE` or `INFINITE`.'
          )
          .optional(),
        lastEventEndDate: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'End date of the last [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nbelonging to the schedule. Identical to `event.end.localDate`. Available only if\n`status` is set to `FINITE`.'
          )
          .optional(),
        adjustedFirstEventStartDate: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Start date of the first [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nbelonging to the schedule, adjusted to `timeZone` of the\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor the `timeZone` specified in the request.\nIdentical to `event.adjustedStart.localDate`.'
          )
          .optional(),
        adjustedLastEventEndDate: z
          .object({
            localDate: z
              .string()
              .describe(
                'Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nWix Calendar APIs ignore seconds.'
              )
              .optional()
              .nullable(),
            timeZone: z
              .string()
              .describe(
                'Time zone in\n[IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).\nFor example, `America/New_York`.'
              )
              .optional()
              .nullable(),
            utcDate: z
              .date()
              .describe(
                'UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).\nFor example, `2024-01-30T13:30:00`.\nNot available for adjusted date fields.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'End date of the last [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/event-object)\nbelonging to the schedule, adjusted to `timeZone` of the\n[site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/properties-object)\nor the `timeZone` specified in the request.\nIdentical to `event.adjustedEnd.localDate`.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the schedule time frame is updated.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
